import { CatalogStandaloneErrorToasts } from '@tableau/data-catalog-client-components';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { ConnectedExplorerSubspaceInitializer } from '~src/spacesPlatform/components/Explorer/helpers/ExplorerSubspaceInitializer';
import { ConnectedShowFilterPanelInitially } from '~src/spacesPlatform/components/Explorer/helpers/ShowFilterPanelInitially';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { SearchSubspaceName, SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';

import { CompositeSessionInfoSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../../app/spaces/RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { AppState } from '../../app/state/AppState';
import { MapStateToProps } from '../../app/utils/ConnectUtils';
import { registerErrorStore, unregisterErrorStore } from '../../catalog/CatalogErrorReporterService';
import { selectCatalogingOnSiteEnabled } from '../../catalog/selectors/CatalogSelectors';
import { DataCatalogUIActionsService } from '../../catalog/services/DataCatalogUIActionsService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ConnectedSearchSpace } from './ConnectedSearchSpace';
import { ConnectedUnifiedSearchSpace } from './ConnectedUnifiedSearchSpace';

export interface SearchSpaceRouterOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly buildSearchComponent: (
    searchSpaceElement: JSX.Element,
    routeProps: RouteComponentProps,
    spaceInfo: SpaceInfo,
    extraChildren?: React.ReactNode) => JSX.Element;
}

export interface SearchSpaceRouterStateProps {
  readonly catalogingOnSiteEnabled: boolean;
  readonly areUsersVisible: boolean;
}

export type SearchSpaceRouterComponentProps = SearchSpaceRouterOwnProps & SearchSpaceRouterStateProps;

export class SearchSpaceRouterComponent extends React.Component<SearchSpaceRouterComponentProps> {
  public render() {
    if (FeatureFlags.crassSearch) {
        return (
            <Switch>
                <Route exact path={this.props.urls.searchPath()} render={this.componentForUnifiedSearch()} />
                <Redirect from={this.props.urls.searchPath() + '/:subspace'} to={{
                    pathname: this.props.urls.searchPath(),
                    search: this.getQueryFromHashPath()
                }} />
                <Route render={this.error} />
            </Switch>
        );
    } else {
      return (
        <Switch>
          <Route exact path={this.props.urls.searchViewsPath()} render={this.componentForRoute('views')} />
          {FeatureFlags.metricsSpaceEnabled &&
            <Route exact path={this.props.urls.searchMetricsPath()} render={this.componentForRoute('metrics')} />
          }
          {FeatureFlags.publishedConnectionContentManagementEnabled &&
            <Route exact path={this.props.urls.searchPublishedConnectionsPath()} render={this.componentForRoute('virtualConnections')} />
          }
          <Route exact path={this.props.urls.searchWorkbooksPath()} render={this.componentForRoute('workbooks')} />
          <Route exact path={this.props.urls.searchDatasourcesPath()} render={this.componentForRoute('datasources')} />
          <Route exact path={this.props.urls.searchFlowsPath()} render={this.componentForRoute('flows')} />
          <Route exact path={this.props.urls.searchProjectsPath()} render={this.componentForRoute('projects')} />
          {this.props.catalogingOnSiteEnabled &&
            <Route exact path={this.props.urls.searchTablesPath()} render={this.componentForRoute('tablesWithCrass' , ErrorToastsForCatalog)} />
          }
          {this.props.catalogingOnSiteEnabled &&
            <Route exact path={this.props.urls.searchDatabasesPath()} render={this.componentForRoute('databasesWithCrass', ErrorToastsForCatalog)} />
          }
          <Route exact path={this.props.urls.searchDataRolesPath()} render={this.componentForRoute('dataRoles')} />
          {this.props.areUsersVisible &&
            <Route exact path={this.props.urls.searchUsersPath()} render={this.componentForRoute('users')} />
          }
          <Route exact path={this.props.urls.searchLensesPath()} render={this.componentForRoute('lenses')} />
          <Redirect exact from={this.props.urls.searchPath()} to={this.props.urls.searchViewsPath()} />
          <Route render={this.error} />
        </Switch>
      );
    }
  }

  private getQueryFromHashPath = () => {
    const values = window.location.hash.split('?');
    return values.length === 2 ? `?${values[1]}` : '';
  }

  private componentForUnifiedSearch(){
    const spaceInfo: SpaceInfo = {
      item: SpaceItemForNonEntitySpace,
      spaceType: 'unifiedSearch',
      subspaceName: undefined
    };

    return (routeProps: RouteComponentProps): JSX.Element => {
      const searchSpaceElement = <ConnectedUnifiedSearchSpace />;
      return this.props.buildSearchComponent(searchSpaceElement, routeProps, spaceInfo);
    };
  }

  private componentForRoute = (subspaceName: SearchSubspaceName, extraChildren?: React.ReactNode) => {
    return (routeProps: RouteComponentProps): JSX.Element => {
      const searchSpaceElement = <ConnectedSearchSpace
        siteName={this.props.siteName}
        subspaceName={subspaceName} />;
      const spaceInfo: SpaceInfo = {
        item: SpaceItemForNonEntitySpace,
        spaceType: 'search',
        subspaceName
      };
      return this.props.buildSearchComponent(searchSpaceElement, routeProps, spaceInfo, extraChildren);
    };
  }

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }
}

// The catalog subspaces catch and render errors via DC3 infrastructure, which we access via
// the CatalogStandaloneErrorToasts component. We must add this component at the router level,
// rather than in the subspace, because it reacts to URL updates from React Router (so it can't
// be hidden from the router by pure components).
const ErrorToastsForCatalog = (
  <CatalogStandaloneErrorToasts
    uiActions={DataCatalogUIActionsService.instance()}
    registerErrorStore={registerErrorStore}
    unregisterErrorStore={unregisterErrorStore}
  />
);

const mapStateToProps: MapStateToProps<SearchSpaceRouterStateProps> = (state: AppState) => ({
  catalogingOnSiteEnabled: selectCatalogingOnSiteEnabled(state),
  areUsersVisible: !CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced(state),
});

// Component must *not* be pure as to allow route updates to flow through
export const ConnectedSearchSpaceRouter: React.ComponentClass<SearchSpaceRouterOwnProps>
  = connect(mapStateToProps, null, null, {pure: false})(SearchSpaceRouterComponent);

function buildSearchComponent(
    searchSpaceElement: JSX.Element,
    routeProps: RouteComponentProps,
    spaceInfo: SpaceInfo,
    extraChildren?: React.ReactNode): JSX.Element {
  const queryUpdaterProps = buildQueryUpdaterProps(routeProps);
  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedExplorerSubspaceInitializer routeLocationSearch={routeProps.location.search} spaceInfo={spaceInfo}>
          <ConnectedShowFilterPanelInitially>
            { searchSpaceElement }
            { extraChildren }
          </ConnectedShowFilterPanelInitially>
        </ConnectedExplorerSubspaceInitializer>
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}

function buildQueryUpdaterProps(routeProps: RouteComponentProps): QueryParamUpdateProviderOwnProps{
  return {
    routeLocationPath: routeProps.location.pathname,
    routeLocationSearch: routeProps.location.search,
    shouldUpdateContentTypes: false,
    shouldUpdateFilters: true,
    shouldUpdateBreadcrumbs: false,
    shouldUpdateSort: true
  };
}

export type SearchSpaceRouterProps = Omit<SearchSpaceRouterOwnProps, 'buildSearchComponent'>;

export const SearchSpaceRouter = (props: SearchSpaceRouterProps) => {
  const routerProps = {
    ...props,
    buildSearchComponent
  };
  return <ConnectedSearchSpaceRouter {...routerProps} />;
};
